import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { Skeleton } from '../../../components/ui/skeleton';
import {
  useDeleteProjectFileMutation,
  useDeleteProjectImageMutation,
  useDeleteProjectQuoteMutation,
  useDownloadProjectMultimediaFileMutation,
  useDownloadProjectMultimediaZipMutation,
  useDuplicateProjectMultimediaMutation,
  useGetProjectQuery,
  useGetProjectsQuery,
} from '../api/projects';
import { downloadEntriesAsZip, triggerBlobDownload } from '../lib/bulkDownload';
import {
  getFileEntries,
  getMediaEntries,
  getPdfEntries,
  isImageEntry,
  isVisualEntry,
  type MediaEntry,
} from '../lib/mediaEntries';
import { applyPresetPrefix, type PresetPrefix } from '../lib/presetPrefixes';
import { useMultimediaSelection } from '../lib/useMultimediaSelection';
import { resolveProjectMultimediaTarget } from '../model/projectMultimedia';
import { useRenameProjectMultimediaMutation } from '../services/projects.api';
import DeleteConfirmDialog, { type DeleteTarget } from './multimedia/DeleteConfirmDialog';
import DocumentSection from './multimedia/DocumentSection';
import FeedbackBanner, { type FeedbackState } from './multimedia/FeedbackBanner';
import ImageSection from './multimedia/ImageSection';
import MediaPreviewDialog from './multimedia/MediaPreviewDialog';
import type { MediaTab } from './multimedia/MediaTabSwitcher';
import MultimediaHeader from './multimedia/MultimediaHeader';
import RenamePrefixDialog, { type RenamePayload } from './multimedia/RenamePrefixDialog';
import SectionCard from './multimedia/SectionCard';
import UndoDeleteToast from './multimedia/UndoDeleteToast';
import UploadDialog from './multimedia/UploadDialog';
import type { UploadKind } from './multimedia/uploadConfig';

const UNDO_COUNTDOWN_SECONDS = 10;

type PendingDeletionKind = 'images' | 'quotes' | 'files';

interface PendingDelete {
  entries: MediaEntry[];
  kind: PendingDeletionKind;
  target: DeleteTarget;
}

interface PendingDeletion {
  entries: MediaEntry[];
  projectId: number;
  kind: PendingDeletionKind;
  countdown: number;
}

type RenameState =
  | { kind: 'single'; entry: MediaEntry; allowPrefix: boolean }
  | { kind: 'bulk'; entries: MediaEntry[] };

const buildDeleteMessage = (successCount: number, errors: string[]) => {
  if (errors.length === 0) {
    const noun = successCount === 1 ? 'archivo eliminado' : 'archivos eliminados';
    return `${successCount} ${noun} correctamente`;
  }
  return `${successCount} eliminados, ${errors.length} con error: ${errors.join(', ')}`;
};

const buildRenameMessage = (total: number, errors: string[]) => {
  if (errors.length === 0) {
    const noun = total === 1 ? 'archivo renombrado' : 'archivos renombrados';
    return `${total} ${noun} correctamente`;
  }
  return `${total - errors.length} renombrados, ${errors.length} con error.`;
};

const buildDuplicateMessage = (count: number, errors: string[]) => {
  if (errors.length === 0) {
    const noun = count === 1 ? 'archivo duplicado' : 'archivos duplicados';
    return `${count} ${noun} correctamente`;
  }
  return `${count - errors.length} duplicados, ${errors.length} con error.`;
};

const buildDuplicateItems = (entries: MediaEntry[]) =>
  entries.map((entry) => ({
    collection: entry.collection,
    fileName: entry.name,
  }));

const MultimediaView = () => {
  const { masterProjectId, subProjectId } = useParams();
  const numericMasterProjectId = Number(masterProjectId);
  const numericSubProjectId = subProjectId === undefined ? null : Number(subProjectId);
  const hasValidRouteParams =
    Number.isFinite(numericMasterProjectId) && (numericSubProjectId === null || Number.isFinite(numericSubProjectId));
  const { data: projects = [], isFetching: isFetchingProjects } = useGetProjectsQuery(undefined, {
    skip: !hasValidRouteParams,
  });
  const multimediaTarget = useMemo(() => {
    if (!hasValidRouteParams) return null;
    return resolveProjectMultimediaTarget(projects, numericMasterProjectId, numericSubProjectId);
  }, [hasValidRouteParams, numericMasterProjectId, numericSubProjectId, projects]);
  const effectiveProjectId = multimediaTarget?.projectId ?? 0;
  const {
    data: project,
    isFetching,
    refetch,
  } = useGetProjectQuery(effectiveProjectId, { skip: multimediaTarget === null });

  const [deleteProjectImage] = useDeleteProjectImageMutation();
  const [deleteProjectQuote] = useDeleteProjectQuoteMutation();
  const [deleteProjectFile] = useDeleteProjectFileMutation();
  const [renameProjectMultimedia] = useRenameProjectMultimediaMutation();
  const [duplicateProjectMultimedia, { isLoading: isDuplicating }] = useDuplicateProjectMultimediaMutation();
  const [triggerZip, { isLoading: isDownloading }] = useDownloadProjectMultimediaZipMutation();
  const [triggerFileDownload] = useDownloadProjectMultimediaFileMutation();

  const selection = useMultimediaSelection();
  const [uploadKind, setUploadKind] = useState<UploadKind | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
  const pendingDeletionRef = useRef<PendingDeletion | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [prefixFilter, setPrefixFilter] = useState<PresetPrefix | null>(null);
  const [mediaTab, setMediaTab] = useState<MediaTab>('images');
  const [renameState, setRenameState] = useState<RenameState | null>(null);
  const [previewEntry, setPreviewEntry] = useState<MediaEntry | null>(null);

  useEffect(() => {
    pendingDeletionRef.current = pendingDeletion;
  }, [pendingDeletion]);

  const visualEntries = useMemo(() => {
    const entries = [
      ...getMediaEntries(project?.images, 'images'),
      ...getMediaEntries(project?.files, 'files').filter(isVisualEntry),
    ];
    if (!pendingDeletion || pendingDeletion.kind !== 'images') return entries;
    const hidden = new Set(pendingDeletion.entries.map((entry) => entry.id));
    return entries.filter((entry) => !hidden.has(entry.id));
  }, [project?.files, project?.images, pendingDeletion]);

  const quoteEntries = useMemo(() => {
    const entries = getPdfEntries(project?.quotes, 'quotes');
    if (!pendingDeletion || pendingDeletion.kind !== 'quotes') return entries;
    const hidden = new Set(pendingDeletion.entries.map((entry) => entry.id));
    return entries.filter((entry) => !hidden.has(entry.id));
  }, [project?.quotes, pendingDeletion]);

  const fileEntries = useMemo(() => {
    const entries = getFileEntries(project?.files, 'files');
    if (!pendingDeletion || pendingDeletion.kind !== 'files') return entries;
    const hidden = new Set(pendingDeletion.entries.map((entry) => entry.id));
    return entries.filter((entry) => !hidden.has(entry.id));
  }, [project?.files, pendingDeletion]);

  const allEntries = useMemo(
    () => [...visualEntries, ...quoteEntries, ...fileEntries],
    [visualEntries, quoteEntries, fileEntries]
  );
  const coverName = visualEntries.find((entry) => entry.collection === 'images' && isImageEntry(entry))?.name ?? null;

  const commitPendingDeletion = useCallback(
    async (pending: PendingDeletion) => {
      const errors: string[] = [];
      let successCount = 0;

      await pending.entries.reduce(async (chain, entry) => {
        await chain;
        try {
          if (entry.collection === 'images') {
            await deleteProjectImage({ projectId: pending.projectId, fileName: entry.name }).unwrap();
          } else if (entry.collection === 'quotes') {
            await deleteProjectQuote({ projectId: pending.projectId, fileName: entry.name }).unwrap();
          } else {
            await deleteProjectFile({ projectId: pending.projectId, fileName: entry.name }).unwrap();
          }
          successCount += 1;
        } catch {
          errors.push(entry.name);
        }
      }, Promise.resolve());

      setPendingDeletion(null);
      await refetch();
      setFeedback({
        variant: errors.length === 0 ? 'success' : 'error',
        message: buildDeleteMessage(successCount, errors),
      });
    },
    [deleteProjectImage, deleteProjectQuote, deleteProjectFile, refetch]
  );

  const enqueueDeletion = useCallback(
    async (kind: PendingDeletionKind, entries: MediaEntry[]) => {
      if (entries.length === 0 || !effectiveProjectId) return;
      const prev = pendingDeletionRef.current;
      if (prev) await commitPendingDeletion(prev);
      setFeedback(null);
      setPendingDeletion({
        projectId: effectiveProjectId,
        kind,
        entries,
        countdown: UNDO_COUNTDOWN_SECONDS,
      });
    },
    [commitPendingDeletion, effectiveProjectId]
  );

  useEffect(() => {
    if (!pendingDeletion) return undefined;
    if (pendingDeletion.countdown <= 0) {
      void commitPendingDeletion(pendingDeletion);
      return undefined;
    }
    const id = setTimeout(() => {
      setPendingDeletion((p) => (p ? { ...p, countdown: p.countdown - 1 } : null));
    }, 1000);
    return () => clearTimeout(id);
  }, [pendingDeletion, commitPendingDeletion]);

  useEffect(
    () => () => {
      const pending = pendingDeletionRef.current;
      if (pending) void commitPendingDeletion(pending);
    },
    [effectiveProjectId, commitPendingDeletion]
  );

  const handleOpenUpload = (kind: UploadKind) => {
    setFeedback(null);
    setUploadKind(kind);
  };

  const handleRequestDelete = (kind: PendingDeletionKind, entry: MediaEntry) => {
    setFeedback(null);
    setPendingDelete({ entries: [entry], kind, target: { mode: 'single', fileName: entry.name } });
  };

  const handleRequestBulkDelete = () => {
    setFeedback(null);
    const entries = visualEntries.filter((entry) => selection.isSelected(entry.id));
    setPendingDelete({ entries, kind: 'images', target: { mode: 'bulk', count: entries.length } });
  };

  const handleRequestBulkRename = () => {
    if (selection.count === 0) return;
    setFeedback(null);
    setRenameState({ kind: 'bulk', entries: visualEntries.filter((entry) => selection.isSelected(entry.id)) });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete || !project) return;
    const { entries, kind, target } = pendingDelete;
    setPendingDelete(null);
    if (target.mode === 'bulk') selection.clear();
    void enqueueDeletion(kind, entries);
  };

  const handleUndoDeletion = () => setPendingDeletion(null);

  const handleBulkDownload = async () => {
    const selectedEntries = visualEntries.filter((entry) => selection.isSelected(entry.id));
    try {
      await downloadEntriesAsZip(effectiveProjectId, selectedEntries, (args) => triggerZip(args).unwrap());
    } catch {
      setFeedback({ variant: 'error', message: 'No se pudo descargar los archivos. Intenta nuevamente.' });
    }
  };

  const handleDownloadEntry = useCallback(
    async (entry: MediaEntry) => {
      try {
        const blob = await triggerFileDownload({ projectId: effectiveProjectId, path: entry.bucketFilePath }).unwrap();
        triggerBlobDownload(blob, entry.name);
      } catch {
        setFeedback({ variant: 'error', message: 'No se pudo descargar el archivo. Intenta nuevamente.' });
      }
    },
    [effectiveProjectId, triggerFileDownload]
  );

  const handleDownloadAll = async () => {
    if (!project || allEntries.length === 0) return;
    try {
      await downloadEntriesAsZip(
        effectiveProjectId,
        allEntries,
        (args) => triggerZip(args).unwrap(),
        `${project.name.replace(/[^a-z0-9_\-. ]/gi, '_')}_multimedia.zip`
      );
    } catch {
      setFeedback({ variant: 'error', message: 'No se pudo descargar toda la multimedia. Intenta nuevamente.' });
    }
  };

  const handleBulkDuplicate = async () => {
    if (!project || selection.count === 0) return;
    const selectedEntries = visualEntries.filter((entry) => selection.isSelected(entry.id));
    try {
      const result = (await duplicateProjectMultimedia({
        projectId: project.id,
        items: buildDuplicateItems(selectedEntries),
      }).unwrap()) as { errors?: string[] };
      const errors = result.errors ?? [];
      selection.clear();
      await refetch();
      setFeedback({
        variant: errors.length === 0 ? 'success' : 'error',
        message: buildDuplicateMessage(selectedEntries.length, errors),
      });
    } catch {
      setFeedback({ variant: 'error', message: 'No se pudo duplicar los archivos. Intenta nuevamente.' });
    }
  };

  const handleDuplicateEntries = async (entries: MediaEntry[]) => {
    if (!project || entries.length === 0) return;
    try {
      const result = (await duplicateProjectMultimedia({
        projectId: project.id,
        items: buildDuplicateItems(entries),
      }).unwrap()) as { errors?: string[] };
      const errors = result.errors ?? [];
      await refetch();
      setFeedback({
        variant: errors.length === 0 ? 'success' : 'error',
        message: buildDuplicateMessage(entries.length, errors),
      });
    } catch {
      setFeedback({ variant: 'error', message: 'No se pudo duplicar los archivos. Intenta nuevamente.' });
    }
  };

  const handleRename = async (payload: RenamePayload) => {
    if (!project || !renameState) return;

    if (payload.kind === 'name') {
      if (renameState.kind !== 'single') return;
      const { entry } = renameState;
      try {
        await renameProjectMultimedia({
          collection: entry.collection,
          projectId: project.id,
          oldName: entry.name,
          newName: payload.newName,
        }).unwrap();
        setFeedback({ variant: 'success', message: buildRenameMessage(1, []) });
      } catch {
        setFeedback({ variant: 'error', message: buildRenameMessage(1, [entry.name]) });
      }
      setRenameState(null);
      await refetch();
      return;
    }

    const entries = renameState.kind === 'single' ? [renameState.entry] : renameState.entries;
    const errors: string[] = [];

    await entries.reduce(async (chain, entry) => {
      await chain;
      const newName = applyPresetPrefix(entry.name, payload.prefix);
      if (newName === entry.name) return;
      try {
        await renameProjectMultimedia({
          collection: entry.collection,
          projectId: project.id,
          oldName: entry.name,
          newName,
        }).unwrap();
      } catch {
        errors.push(entry.name);
      }
    }, Promise.resolve());

    setRenameState(null);
    if (renameState.kind === 'bulk') selection.clear();
    await refetch();
    setFeedback({
      variant: errors.length === 0 ? 'success' : 'error',
      message: buildRenameMessage(entries.length, errors),
    });
  };

  let renameFileNames: string[];
  if (renameState === null) {
    renameFileNames = [];
  } else {
    renameFileNames =
      renameState.kind === 'single' ? [renameState.entry.name] : renameState.entries.map((entry) => entry.name);
  }

  if (!hasValidRouteParams) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-text-muted">
          Selecciona una inmobiliaria en el sidebar para gestionar su multimedia.
        </p>
      </div>
    );
  }

  if ((isFetchingProjects || isFetching) && !project) {
    return (
      <section className="flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-8 w-72" />
        </header>
        <Skeleton className="h-80 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </section>
    );
  }

  if (multimediaTarget === null || !project) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-text-muted">No se encontró el proyecto seleccionado.</p>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <MultimediaHeader
        isDownloading={isDownloading}
        onDownloadAll={() => {
          void handleDownloadAll();
        }}
        project={project}
        totalFiles={allEntries.length}
      />
      <FeedbackBanner feedback={feedback} />
      {pendingDeletion && (
        <UndoDeleteToast
          countdown={pendingDeletion.countdown}
          count={pendingDeletion.entries.length}
          kind={pendingDeletion.kind}
          onUndo={handleUndoDeletion}
        />
      )}

      <div className="flex flex-col gap-5">
        <SectionCard count={visualEntries.length} onUpload={() => handleOpenUpload('images')} title="Imágenes y Videos">
          <ImageSection
            coverName={coverName}
            entries={visualEntries}
            filter={{ active: prefixFilter, onChange: setPrefixFilter }}
            isDeleting={isDuplicating || isDownloading}
            onBulkDownload={() => {
              void handleBulkDownload();
            }}
            onBulkDuplicate={() => {
              void handleBulkDuplicate();
            }}
            onDownload={handleDownloadEntry}
            onPreview={setPreviewEntry}
            onRequestBulkDelete={handleRequestBulkDelete}
            onRequestBulkRename={handleRequestBulkRename}
            onRequestDelete={(entry) => handleRequestDelete('images', entry)}
            onRequestRename={(entry) => setRenameState({ kind: 'single', entry, allowPrefix: true })}
            onTabChange={(next) => {
              if (next !== mediaTab) selection.clear();
              setMediaTab(next);
            }}
            selection={selection}
            tab={mediaTab}
          />
        </SectionCard>

        <DocumentSection
          accent="error"
          emptyText="No hay cotizaciones cargadas."
          entries={quoteEntries}
          isDeleting={isDuplicating}
          onDownload={handleDownloadEntry}
          onRequestDuplicate={(entry) => {
            void handleDuplicateEntries([entry]);
          }}
          onRequestDelete={(entry) => handleRequestDelete('quotes', entry)}
          onRequestRename={(entry) => setRenameState({ kind: 'single', entry, allowPrefix: false })}
          onUpload={() => handleOpenUpload('quotes')}
          title="Cotizaciones"
        />

        <DocumentSection
          accent="brand"
          emptyText="No hay archivos cargados."
          entries={fileEntries}
          isDeleting={isDuplicating}
          onDownload={handleDownloadEntry}
          onRequestDuplicate={(entry) => {
            void handleDuplicateEntries([entry]);
          }}
          onRequestDelete={(entry) => handleRequestDelete('files', entry)}
          onRequestRename={(entry) => setRenameState({ kind: 'single', entry, allowPrefix: false })}
          onUpload={() => handleOpenUpload('files')}
          subtitle="Documentos: PDF, DOC, XLS, PPT · Imágenes/Video: JPG, PNG, MP4, 3GP · Audio (nota de voz WhatsApp): OGG, OPUS, AAC, AMR, MP3, M4A"
          title="Archivos"
        />
      </div>

      <UploadDialog
        kind={uploadKind}
        onOpenChange={(open) => {
          if (!open) setUploadKind(null);
        }}
        onUploaded={(_, variant, message) => {
          setFeedback({ variant, message });
          void refetch();
        }}
        projectId={project.id}
      />

      <DeleteConfirmDialog
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        open={!!pendingDelete}
        target={pendingDelete?.target ?? null}
      />

      <RenamePrefixDialog
        allowPrefix={renameState === null || renameState.kind === 'bulk' || renameState.allowPrefix}
        fileNames={renameFileNames}
        onCancel={() => setRenameState(null)}
        onConfirm={handleRename}
        open={renameState !== null}
      />

      <MediaPreviewDialog
        entry={previewEntry}
        onOpenChange={(open) => {
          if (!open) setPreviewEntry(null);
        }}
      />
    </section>
  );
};

export default MultimediaView;
